// Client-facing invoice status page — lists all manual invoices billed to
// the signed-in user's email, shows amounts due, pay buttons, PDF download,
// and an expandable payment-history audit per invoice (from PayFast ITN log).
// Auto-refreshes on webhook status changes via Supabase Realtime.

import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, CreditCard, Download, FileText, ChevronDown, ChevronUp, History } from "lucide-react";
import { listMyInvoices, getInvoicePaymentHistory } from "@/lib/api/invoice-payment.functions";
import { launchPayfastCheckout } from "@/lib/payfast-launch";

export const Route = createFileRoute("/_authenticated/portal/invoices")({
  head: () => ({ meta: [{ title: "My invoices · NDLCS" }, { name: "robots", content: "noindex" }] }),
  component: MyInvoicesPage,
});

const rZAR = (c: number) =>
  `R${(c / 100).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function MyInvoicesPage() {
  const list = useServerFn(listMyInvoices);
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["my-invoices"],
    queryFn: () => list(),
    refetchInterval: 45_000,
  });
  const [paying, setPaying] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<"all" | "outstanding" | "paid">("all");
  const [expanded, setExpanded] = useState<string | null>(null);

  // Realtime: refresh the list whenever this user's invoices change (e.g. PayFast ITN flips status to "paid").
  useEffect(() => {
    const email = data?.email;
    if (!email) return;
    const ch = supabase
      .channel(`invoices:${email}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "manual_invoices", filter: `client_email=eq.${email}` },
        () => { qc.invalidateQueries({ queryKey: ["my-invoices"] }); },
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [data?.email, qc]);

  const invoices = data?.invoices ?? [];
  const filtered = invoices.filter((i: any) =>
    statusFilter === "all"
      ? true
      : statusFilter === "outstanding"
        ? i.status !== "paid" && i.status !== "cancelled"
        : i.status === "paid",
  );

  async function pay(ref: string) {
    setPaying(ref);
    try {
      await launchPayfastCheckout(ref);
    } finally {
      setTimeout(() => setPaying(null), 4000);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl text-navy">My invoices</h1>
          <p className="text-sm text-muted-foreground">
            All invoices billed to {data?.email ?? "your account email"}. Pay outstanding balances online and download PDFs.
          </p>
        </div>
        <Link to="/portal/orders" className="text-xs text-primary underline">← Back to orders</Link>
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Card>
          <CardContent className="py-4">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Outstanding</p>
            <p className="font-display text-2xl text-amber-700">{rZAR(data?.outstanding_cents ?? 0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Paid to date</p>
            <p className="font-display text-2xl text-emerald-700">{rZAR(data?.paid_cents ?? 0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Invoices on file</p>
            <p className="font-display text-2xl text-navy">{invoices.length}</p>
          </CardContent>
        </Card>
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        {(["all", "outstanding", "paid"] as const).map((k) => (
          <Button key={k} size="sm" variant={statusFilter === k ? "default" : "outline"} onClick={() => setStatusFilter(k)}>
            {k[0].toUpperCase() + k.slice(1)}
          </Button>
        ))}
        <Button size="sm" variant="ghost" onClick={() => refetch()}>Refresh</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Invoices</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground">No invoices in this view.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-3">Reference</th>
                    <th className="py-2 pr-3">Issued</th>
                    <th className="py-2 pr-3">Due</th>
                    <th className="py-2 pr-3">Total</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filtered.map((inv: any) => {
                    const outstanding = inv.status !== "paid" && inv.status !== "cancelled";
                    const isOpen = expanded === inv.reference;
                    return (
                      <React.Fragment key={inv.id}>
                        <tr>
                          <td className="py-2 pr-3 font-mono text-xs">{inv.reference}</td>
                          <td className="py-2 pr-3">{inv.issued_date}</td>
                          <td className="py-2 pr-3">{inv.due_date ?? "—"}</td>
                          <td className="py-2 pr-3 font-medium">{rZAR(inv.total_cents)}</td>
                          <td className="py-2 pr-3">
                            <Badge variant={inv.status === "paid" ? "default" : outstanding ? "secondary" : "outline"}>
                              {inv.status}
                            </Badge>
                          </td>
                          <td className="py-2 pr-3">
                            <div className="flex flex-wrap gap-2">
                              {outstanding ? (
                                <Button size="sm" onClick={() => pay(inv.reference)} disabled={paying === inv.reference}>
                                  {paying === inv.reference ? (
                                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                  ) : (
                                    <CreditCard className="mr-1 h-3 w-3" />
                                  )}
                                  Pay now
                                </Button>
                              ) : null}
                              <a
                                href={`/pay/invoice/${inv.reference}?t=${inv.public_token}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center rounded-md border border-border px-2 py-1 text-xs hover:bg-secondary"
                              >
                                <FileText className="mr-1 h-3 w-3" /> View
                              </a>
                              <a
                                href={`/api/public/invoice-pdf?ref=${encodeURIComponent(inv.reference)}&t=${inv.public_token}`}
                                className="inline-flex items-center rounded-md border border-border px-2 py-1 text-xs hover:bg-secondary"
                              >
                                <Download className="mr-1 h-3 w-3" /> PDF
                              </a>
                              <button
                                type="button"
                                onClick={() => setExpanded(isOpen ? null : inv.reference)}
                                className="inline-flex items-center rounded-md border border-border px-2 py-1 text-xs hover:bg-secondary"
                                aria-expanded={isOpen}
                              >
                                <History className="mr-1 h-3 w-3" /> History
                                {isOpen ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />}
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isOpen ? (
                          <tr key={`${inv.id}-hist`} className="bg-muted/30">
                            <td colSpan={6} className="px-3 py-3">
                              <PaymentHistoryPanel reference={inv.reference} />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PaymentHistoryPanel({ reference }: { reference: string }) {
  const fetchHistory = useServerFn(getInvoicePaymentHistory);
  const { data, isLoading } = useQuery({
    queryKey: ["invoice-history", reference],
    queryFn: () => fetchHistory({ data: { reference } }),
    staleTime: 15_000,
  });
  const events = data?.events ?? [];
  if (isLoading) {
    return <p className="text-xs text-muted-foreground"><Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Loading payment history…</p>;
  }
  if (events.length === 0) {
    return <p className="text-xs text-muted-foreground">No PayFast webhook events recorded yet for this invoice.</p>;
  }
  const statusColor = (s: string) => {
    const v = (s || "").toLowerCase();
    if (v === "complete" || v === "paid") return "text-emerald-700";
    if (v === "failed" || v === "cancelled") return "text-rose-700";
    return "text-muted-foreground";
  };
  function exportCsv() {
    const header = ["Timestamp", "PayFast ID", "Status", "Mode", "Signature", "Match", "Grant", "Grant error", "Processing ms"];
    const rows = events.map((e: any) => [
      new Date(e.created_at).toISOString(),
      e.pf_payment_id ?? "",
      e.payment_status ?? "",
      e.mode ?? "",
      e.signature_valid ? "valid" : "invalid",
      e.match_kind ?? "",
      e.grant_status ?? e.webhook_outcome ?? "",
      e.grant_error ?? "",
      e.processing_ms ?? "",
    ]);
    const escape = (v: any) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [header, ...rows].map((r) => r.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoice-${reference}-payment-history.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Payment history · {events.length} event(s)</p>
        <button
          type="button"
          onClick={exportCsv}
          className="inline-flex items-center rounded-md border border-border px-2 py-1 text-[11px] hover:bg-secondary"
        >
          <Download className="mr-1 h-3 w-3" /> Export CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1 pr-3">Timestamp</th>
              <th className="py-1 pr-3">PayFast ID</th>
              <th className="py-1 pr-3">Status</th>
              <th className="py-1 pr-3">Mode</th>
              <th className="py-1 pr-3">Signature</th>
              <th className="py-1 pr-3">Outcome</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {events.map((e: any) => (
              <tr key={e.id}>
                <td className="py-1 pr-3 font-mono">{new Date(e.created_at).toLocaleString("en-ZA")}</td>
                <td className="py-1 pr-3 font-mono">{e.pf_payment_id ?? "—"}</td>
                <td className={`py-1 pr-3 font-medium ${statusColor(e.payment_status)}`}>{e.payment_status ?? "—"}</td>
                <td className="py-1 pr-3">{e.mode ?? "—"}</td>
                <td className="py-1 pr-3">{e.signature_valid ? "✓ valid" : "✗ invalid"}</td>
                <td className="py-1 pr-3">
                  {e.grant_status ?? e.webhook_outcome ?? "—"}
                  {e.grant_error ? <span className="ml-1 text-rose-700">({e.grant_error})</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
